/**
 *
 * TabParents
 *
 */

import React, { memo, useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

// antd component
import { Form, Select } from "antd";

// assets
import CustomIcons from "components/CustomIcons";
import { BellOutlined } from "@ant-design/icons";

// components
import CustomTable from "components/CustomTable";
import AddItemCard from "components/AddItemCard";

import SelectFieldAxios from "containers/SelectFieldAxios";

// utils
import {
  openNewRow,
  starRowDataActions,
  confirmSaveRowData,
  confirmRemoveRowData,
} from "components/Tabs/helper.js";

// helper for forms
import { getObjId } from "utils/helpers/helper.general";

function TabParents({
  parents,
  sourceData,
  // actions
  updateSourse,
  // default
  className,
}) {
  const childClassNames = classNames("tab-parents-wrapper row-tabs", className);
  const { t, i18n } = useTranslation();
  
  const [currentParents, setCurrentParents] = useState(parents || []);
  const [currentSourse, setCurrentSourse] = useState(sourceData || false);

  const [tableRows, setTableRow] = useState([]);
  const [newElement, setNewElement] = useState([]);
  const [showError, setShowError] = useState(false);

  // Form initialization for new row
  const [addNewFrom] = Form.useForm();

  // Set data for opened item
  useEffect(() => {
    setCurrentSourse(sourceData);
  }, [sourceData]);

  // Set data for opened item
  useEffect(() => {
    setCurrentParents(parents);

    const rows =
      parents?.length > 0 &&
      parents
        ?.map(elem => {
          return {
            ...elem,
            firstname: elem?.description?.firstname,
            lastname: elem?.description?.lastname,
          };
        })
        .sort((a, b) => a?.lastname.localeCompare(b?.lastname));
    setTableRow(rows);
    setCurrentSourse(sourceData);
  }, []);

  // Set data for opened item
  useEffect(() => {
    // set parents list
    if (JSON.stringify(currentParents) !== JSON.stringify(parents)) {
      setCurrentParents(parents);
      const rows = parents
        ?.map(elem => {
          return {
            ...elem,
            firstname: elem?.description?.firstname,
            lastname: elem?.description?.lastname,
          };
        })
        .sort((a, b) => a?.lastname.localeCompare(b?.lastname));
      setTableRow(rows);
    }
  }, [parents, currentParents]);

  // create new Row with form
  const makeNewRow = useMemo(() => {
    // initial data for SelectFieldAxios component
    const newElementSelect = {
      label: `${t("common.new")} ${t("labels.parent")}`,
      name: "new_parent",
      endponit: "users/role",
      endponitSend: {
        role: "parent",
      },
      required: true,
      displayLabel: false,
      value: "",
      type: "axios-select",
      col: 24,
      onSelect: (LabeledValue, option) => {
        setNewElement(option?.item);
      },
      itemCallback: item => {
        return {
          key: item._id,
          label: `${item?.description?.lastname} ${
            item?.description?.firstname
          }`,
          value: item._id,
          item: item,
        };
      },
    };

    return (
      <Form
        form={addNewFrom}
        name="add-new-element-parent"
        autoComplete="off"
      >
        <SelectFieldAxios
          className="table-select"
          endponit={newElementSelect.endponit}
          endponitSend={newElementSelect.endponitSend}
          itemCallback={newElementSelect.itemCallback}
          onSelect={newElementSelect.onSelect}
          item={newElementSelect}
          getPopupContainer={false}
        />
        {showError && (
          <span className="alert">{t("common.existInList")}</span>
        )}
      </Form>
    );
  }, [addNewFrom, showError, setNewElement, i18n?.language]);

  // notifications form
  const notificationsFrom = record => {
    const selectName = `notifications-for-${record?._id}`;
    return (
      <Select
        name={selectName}
        value={record?.notification_type}
        className="table-select"
        placeholder={t("notifications.selectNotif")}
        onSelect={(LabeledValue, option) => {
          setContactNotificationType(record, LabeledValue);
        }}
        getPopupContainer={trigger => trigger.parentElement}
      >
        {/*<Select.Option key={`${record?._id}-email`} value="email">*/}
        {/*  Email*/}
        {/*</Select.Option>*/}
        {/*<Select.Option key={`${record?._id}-email-app`} value="email-app">*/}
        {/*  Email & Notification Push*/}
        {/*</Select.Option>*/}
        {/*TODO: TEMPORARY SOLUTION UNTIL NOTIFICATION BY EMAIL*/}
        <Select.Option key={`${record?._id}-app`} value="email-app">
          {t("notifications.pushNotif")}
        </Select.Option>
      </Select>
    );
  };

  // Remove and update current Element
  const removeElement = elementObj => {
    if (!!elementObj?._id) {
      const newElements =
        currentParents.length > 0 &&
        currentParents.filter(el => el._id !== elementObj._id);
      const isContactPerson =
        getObjId(elementObj) == getObjId(source?.contact_person);

      setCurrentParents(newElements);
      let source = { ...currentSourse };
      if ((newElements && newElements?.length <= 0) || !!isContactPerson) {
        source = {
          ...source,
          parents: newElements,
          contact_person: null,
        };
      } else {
        source = {
          ...source,
          parents: newElements,
        };
      }
      setCurrentSourse(source);
      updateSourse(source);
    }
  };

  // save/add new Element form new Row
  const addElement = () => {
    addNewFrom.submit();
    const val = addNewFrom.getFieldsValue();
    if (val.new_parent && newElement) {
      const hasElement =
        currentParents &&
        currentParents.some(element => element._id === newElement._id);

      if (!hasElement) {
        setShowError(false);
        const newEl = {
          _id: newElement?._id,
          description: newElement?.description,
        };
        const newEls = [newEl, ...currentParents];
        setCurrentParents(newEls);

        const sourceNew = { ...currentSourse, parents: newEls };
        setCurrentSourse(sourceNew);
        updateSourse(sourceNew);
      } else {
        setShowError(true);
      }
    }
  };

  const setContactNotificationType = (record, option) => {
    let isChanged = false;
    let newParents = currentParents.map(parent => {
      if (parent?._id == record?._id && parent.notification_type !== option) {
        const parentSource = {
          ...parent,
          notification_type: option,
        };
        isChanged = true;
        return parentSource;
      } else {
        return parent;
      }
    });

    if (isChanged) {
      setCurrentParents(newParents);

      const source = { ...currentSourse, parents: newParents };
      setCurrentSourse(source);
      updateSourse(source);
    }
  };

  const setContactPerson = record => {
    if (!!record?.role && record.role === "parent") {
      const tempSource = {
        ...currentSourse,
        contact_person: record,
      };
      const isContactPerson =
        getObjId(record) == getObjId(currentSourse?.contact_person);
      if (!!isContactPerson) {
        tempSource.contact_person = null;
      }
      setCurrentSourse(tempSource);
      updateSourse(tempSource);
    }
  };

  const actionsColumn = {
    title: "",
    dataIndex: "action",
    key: "action-column-parent",
    className: "column-actions",
    render: (_, record) => {
      if (record.key === "initial") {
        return (
          <div className="row-actions">
            {confirmSaveRowData(tableRows, setTableRow, addElement)}
          </div>
        );
      } else {
        const isContactPerson =
          getObjId(record) == getObjId(currentSourse?.contact_person);

        const upadateRowAction = {
          record: record,
          rows: tableRows,
          setRows: setTableRow,
          confirmHandle: setContactPerson,
          checked: isContactPerson,
          msg: t("students.makeMainContactAsk"),
        };
        return (
          <div className="row-actions">
            {starRowDataActions(upadateRowAction)}
            {/* parents?.length > 1 &&  */ confirmRemoveRowData(
              record,
              removeElement,
            )}
          </div>
        );
      }
    },
  };

  const columns = [
    {
      title: (
        <div className="table-title">
          <CustomIcons type="person" />{" "}
          <span className="name">{t("labels.parent")}</span>
        </div>
      ),
      dataIndex: "parent",
      key: "column-parent",
      className: "column-parent",
      sorter: (a, b) => a?.lastname.localeCompare(b?.lastname),
      render: (text, record) => {
        if (record.key === "initial") {
          return {
            children: makeNewRow,
            props: {
              colSpan: 2,
            },
          };
        } else {
          const isContactPerson =
            getObjId(record) == getObjId(currentSourse?.contact_person);
          return {
            children: (
              <>
                <Link
                  className="link-table"
                  to={`/utilisateurs/${record._id}`}
                >
                  {record?.lastname} {record?.firstname}
                </Link>
                {isContactPerson && (
                  <span className="additional-info">
                    {t("students.mainContact")}
                  </span>
                )}
              </>
            ),
            props: {
              colSpan: 1,
              className: isContactPerson && "contact-persone",
            },
          };
        }
      },
    },
    {
      title: (
        <div className="table-title">
          <BellOutlined />{" "}
          <span className="name">{t("labels.notification")}</span>
        </div>
      ),
      dataIndex: "notification",
      key: "column-notification",
      className: "column-notification",
      render: (text, record, index) => {
        if (record.key === "initial") {
          return {
            children: "",
            props: {
              colSpan: 0,
            },
          };
        } else {
          return {
            children: notificationsFrom(record),
            props: {
              colSpan: 1,
            },
          };
        }
      },
    },
  ];

  // Set action column object
  columns.push(actionsColumn);

  // Render
  return (
    <div className={childClassNames}>
      <div className="row">
        <AddItemCard
          type="section"
          title={t("labels.parents")}
          addAction={() => openNewRow(tableRows, setTableRow, addNewFrom)}
        />
      </div>
      <div className="row">
        <CustomTable dataSource={tableRows || []} columns={columns} />
      </div>
    </div>
  );
}

TabParents.propTypes = {
  parents: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.object.isRequired,
    PropTypes.array.isRequired,
  ]),
  sourceData: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.object.isRequired,
  ]),
  updateSourse: PropTypes.func.isRequired,
};

export default memo(TabParents);
